import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { Star, Camera, MessageCircle, Check } from "lucide-react";
import { api, waLink } from "@/lib/api";
import { useT } from "@/lib/i18n";

const TX = {
  pt: {
    eyebrow: "A sua opinião", title: (n) => `${n ? `${n}, ` : ""}como foi a sua visita?`, sub: (w) => `Conte-nos como correu a visita à boutique${w ? ` e o que achou do ${w}` : ""}. Demora um minuto.`,
    stars: "Avaliação geral", exp: "Como foi a experiência na boutique?", exp_ph: "Atendimento, ambiente, tempo que lhe dedicámos…",
    qual: "O que achou da qualidade do relógio?", qual_ph: "Acabamentos, conforto no pulso, como se sente a usá-lo…", com: "Mais alguma coisa que queira partilhar?",
    photo: "Uma foto a usar o relógio (opcional)", photo_btn: "Escolher fotografia", consent: "Autorizo a GoldenTime a publicar no site, junto do relógio, a minha opinião, o meu primeiro nome e a fotografia.",
    send: "Enviar opinião", sending: "A enviar…", thanks: "Obrigado pela sua opinião.", thanks_sub: "A equipa GoldenTime agradece o tempo que dedicou. Se autorizou a publicação, a sua opinião poderá aparecer no site depois de revista.",
    or: "Prefere o WhatsApp?", wa_btn: "Enviar foto e comentário por WhatsApp", invalid: "Esta ligação não é válida ou expirou.", need_star: "Escolha de 1 a 5 estrelas.",
    wa_text: (ref) => `Olá GoldenTime, aqui fica a minha opinião sobre a visita (ref. ${ref}):\n1. A experiência na boutique: \n2. A qualidade do relógio: \n3. Autorizo / não autorizo a publicação no site.\n(Junto uma foto a usar o relógio)`,
  },
  en: {
    eyebrow: "Your opinion", title: (n) => `${n ? `${n}, ` : ""}how was your visit?`, sub: (w) => `Tell us about your boutique visit${w ? ` and what you thought of the ${w}` : ""}. It takes a minute.`,
    stars: "Overall rating", exp: "How was your experience at the boutique?", exp_ph: "Service, atmosphere, the time we dedicated to you…",
    qual: "What did you think of the watch's quality?", qual_ph: "Finishing, comfort on the wrist, how it feels to wear…", com: "Anything else you would like to share?",
    photo: "A photo wearing the watch (optional)", photo_btn: "Choose photo", consent: "I authorise GoldenTime to publish my review, first name and photo on the website, next to the watch.",
    send: "Send review", sending: "Sending…", thanks: "Thank you for your review.", thanks_sub: "The GoldenTime team is grateful for your time. If you authorised publication, your review may appear on the website once reviewed.",
    or: "Prefer WhatsApp?", wa_btn: "Send photo and comment via WhatsApp", invalid: "This link is not valid or has expired.", need_star: "Choose from 1 to 5 stars.",
    wa_text: (ref) => `Hello GoldenTime, here is my review of the visit (ref. ${ref}):\n1. The boutique experience: \n2. The watch's quality: \n3. I authorise / do not authorise publication on the website.\n(I attach a photo wearing the watch)`,
  },
};

const Stars = ({ v, set }) => (
  <div className="flex gap-2" role="radiogroup" data-testid="review-stars">
    {[1, 2, 3, 4, 5].map((n) => (
      <button type="button" key={n} onClick={() => set(n)} aria-label={`${n}`} className="transition-transform duration-200 hover:scale-110" data-testid={`review-star-${n}`}>
        <Star className={`w-8 h-8 ${n <= v ? "fill-[#C8AE6E] text-[#C8AE6E]" : "text-[#5a554c]"}`} strokeWidth={1.2} />
      </button>
    ))}
  </div>
);

const Area = ({ label, ph, v, set, testid }) => (
  <label className="block"><span className="eyebrow">{label}</span><textarea className="field mt-3 min-h-[90px]" maxLength={1500} placeholder={ph} value={v} onChange={(e) => set(e.target.value)} data-testid={testid} /></label>
);

export default function Review() {
  const { token } = useParams();
  const { lang: siteLang } = useT();
  const [info, setInfo] = useState(undefined);
  const [f, setF] = useState({ stars: 0, experience: "", quality: "", comment: "", consent: false });
  const [photo, setPhoto] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);
  useEffect(() => { api.get(`/public/reviews/form/${token}`).then((x) => setInfo(x.data)).catch(() => setInfo(null)); }, [token]);
  const L = TX[info?.lang === "en" || (!info && siteLang === "en") ? "en" : "pt"];
  const set = (k) => (v) => { setF((p) => ({ ...p, [k]: v })); if (k === "stars") setErr(""); };
  const preview = useMemo(() => (photo ? URL.createObjectURL(photo) : null), [photo]);
  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);
  const submit = async (e) => {
    e.preventDefault();
    if (!f.stars) return setErr(L.need_star);
    setBusy(true); setErr("");
    const fd = new FormData();
    fd.append("stars", f.stars); fd.append("experience", f.experience); fd.append("quality", f.quality); fd.append("comment", f.comment); fd.append("consent_publish", f.consent);
    if (photo) fd.append("photo", photo);
    try { await api.post(`/public/reviews/form/${token}`, fd); setDone(true); } catch (e2) { setErr(e2?.response?.data?.detail || "Erro"); }
    setBusy(false);
  };
  if (info === undefined) return <div className="min-h-[60vh]" />;
  if (info === null) return <div className="max-w-[760px] mx-auto px-5 sm:px-10 pt-24 min-h-[50vh]" data-testid="review-invalid"><p className="font-display text-3xl text-[#F3EFE6]">{L.invalid}</p></div>;
  if (done || info.submitted) return (
    <div className="max-w-[760px] mx-auto px-5 sm:px-10 pt-24 min-h-[50vh] reveal" data-testid="review-done">
      <Check className="w-10 h-10 text-[#C8AE6E]" strokeWidth={1.2} />
      <h1 className="mt-6 font-display text-4xl sm:text-5xl text-[#F3EFE6]">{L.thanks}</h1>
      <p className="mt-5 text-[#A39E93] max-w-xl">{L.thanks_sub}</p>
    </div>
  );
  return (
    <div className="max-w-[760px] mx-auto px-5 sm:px-10 pt-16 pb-24" data-testid="review-page">
      <p className="eyebrow">{L.eyebrow} · {info.ref}</p>
      <h1 className="mt-4 font-display text-4xl sm:text-5xl text-[#F3EFE6]">{L.title(info.first_name)}</h1>
      <p className="mt-5 text-[#A39E93]">{L.sub(info.watch_label)}</p>
      <form onSubmit={submit} className="mt-12 space-y-10">
        <div><span className="eyebrow">{L.stars}</span><div className="mt-4"><Stars v={f.stars} set={set("stars")} /></div></div>
        <Area label={L.exp} ph={L.exp_ph} v={f.experience} set={set("experience")} testid="review-experience" />
        <Area label={L.qual} ph={L.qual_ph} v={f.quality} set={set("quality")} testid="review-quality" />
        <Area label={L.com} ph="" v={f.comment} set={set("comment")} testid="review-comment" />
        <div>
          <span className="eyebrow">{L.photo}</span>
          <label className="mt-4 flex items-center gap-4 cursor-pointer">
            {photo ? <img src={preview} alt="" className="w-24 h-24 object-cover border border-[#C8AE6E]/40" data-testid="review-photo-preview" /> : <span className="w-24 h-24 border border-dashed border-[#C8AE6E]/40 flex items-center justify-center"><Camera className="w-6 h-6 text-[#C8AE6E]" strokeWidth={1.2} /></span>}
            <span className="btn-line !py-2 text-xs">{L.photo_btn}</span>
            <input type="file" accept="image/*" className="hidden" onChange={(e) => setPhoto(e.target.files?.[0] || null)} data-testid="review-photo-input" />
          </label>
        </div>
        <label className="flex items-start gap-3 text-sm text-[#A39E93]"><input type="checkbox" className="mt-1 accent-[#C8AE6E]" checked={f.consent} onChange={(e) => set("consent")(e.target.checked)} data-testid="review-consent" />{L.consent}</label>
        {err && <p className="text-sm text-[#e0937f]" role="alert" data-testid="review-error">{err}</p>}
        <button className="btn-gold" disabled={busy} data-testid="review-submit">{busy ? L.sending : L.send}</button>
      </form>
      <div className="mt-16 border-t border-[#282520] pt-10">
        <p className="eyebrow">{L.or}</p>
        <a href={waLink(info.whatsapp_number, L.wa_text(info.ref))} target="_blank" rel="noreferrer" className="btn-line mt-5 inline-flex" data-testid="review-whatsapp-button"><MessageCircle className="w-4 h-4" /> {L.wa_btn}</a>
      </div>
    </div>
  );
}
